import { useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { usePeople } from '../../People/hooks';
import { useToast } from '../../../utils/useToast';
import { Input } from '../../../ui/form/Input';
import {
  ChevronRightIcon,
  ClockIcon,
  UserGroupIcon,
} from '@heroicons/react/20/solid';
import { AssignEmployee } from './AssignEmployee';
import rota from '../../../network/rota';
import { Modal } from '../../../ui/Modal';
import { Button } from '../../../ui/Button';
import type { IPerson } from '../../../types/people';

const TIME_PRESETS = [
  { label: '09:00', value: '09:00' },
  { label: '10:00', value: '10:00' },
  { label: '12:00', value: '12:00' },
  { label: '17:00', value: '17:00' },
];

const SHIFT_PRESETS = [
  { label: 'Morning', start: '08:00', end: '16:00' },
  { label: 'Day', start: '09:00', end: '17:00' },
  { label: 'Late', start: '12:00', end: '20:00' },
  { label: 'Short', start: '10:00', end: '14:00' },
];

function toMinutes(time: string) {
  if (!time || !/^\d{2}:\d{2}$/.test(time)) return null;
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

function addHours(time: string, hours: number) {
  const totalMinutes = toMinutes(time);
  if (totalMinutes === null) return '';
  const nextMinutes = totalMinutes + hours * 60;
  const normalized = ((nextMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const nextHours = String(Math.floor(normalized / 60)).padStart(2, '0');
  const nextMins = String(normalized % 60).padStart(2, '0');
  return `${nextHours}:${nextMins}`;
}

function formatDuration(start: string, end: string) {
  const startMinutes = toMinutes(start);
  const endMinutes = toMinutes(end);

  if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) {
    return null;
  }

  const durationMinutes = endMinutes - startMinutes;
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;

  if (minutes === 0) {
    return `${hours}h`;
  }

  if (hours === 0) {
    return `${minutes}m`;
  }

  return `${hours}h ${minutes}m`;
}

const colorOptions = [
  {
    id: '1',
    hex: '#F4F4F5',
  },
  {
    id: '2',
    hex: '#BE185D',
  },
  {
    id: '3',
    hex: '#A21CAF',
  },
  {
    id: '4',
    hex: '#7E22CE',
  },
  {
    id: '5',
    hex: '#6D28D9',
  },
  {
    id: '6',
    hex: '#4338CA',
  },
  {
    id: '7',
    hex: '#1D4ED8',
  },
  {
    id: '8',
    hex: '#0369A1',
  },
  {
    id: '9',
    hex: '#0E7490',
  },
  {
    id: '10',
    hex: '#0F766E',
  },
  {
    id: '11',
    hex: '#047857',
  },
  {
    id: '12',
    hex: '#15803D',
  },
];

export default function CreateNewRotaModal({ open, setOpen, refetchRotas }: any) {
  const [isShownAssignEmployees, setIsShownAssignEmployyes] = useState(false);

  const { data: peopleResponse = { data: [] as IPerson[] } } = usePeople();
  const peopleOptions: IPerson[] = peopleResponse?.data || [];
  const { toast } = useToast();

  const closeModal = () => {
    setOpen(false);
    setIsShownAssignEmployyes(false);
    formik.resetForm();
  };

  type CreateRotaFormValues = {
    name: string;
    start_time: string;
    end_time: string;
    employees: string[];
    color: string;
    start_date: string;
    end_date: string;
  };

  const formik = useFormik<CreateRotaFormValues>({
    initialValues: {
      name: 'Default',
      start_time: '09:00',
      end_time: '17:00',
      employees: [],
      color: '#F4F4F5',
      start_date: '',
      end_date: '',
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      name: yup.string().trim().required('Rota name is required'),
      start_time: yup.string().required('Start time is required'),
      end_time: yup
        .string()
        .required('End time is required')
        .test(
          'is-after-start',
          'End time must be after start time',
          function validateEndTime(value) {
            const { start_time } = this.parent;
            const startMinutes = toMinutes(start_time);
            const endMinutes = toMinutes(value || '');

            if (startMinutes === null || endMinutes === null) {
              return true;
            }

            return endMinutes > startMinutes;
          },
        ),
      start_date: yup.string().required('Start date is required'),
      end_date: yup.string().test(
        'end-date-after-start',
        'End date must be on or after the start date',
        function validateEndDate(value) {
          const { start_date } = this.parent;
          if (!value || !start_date) return true;
          return value >= start_date;
        },
      ),
    }),
    onSubmit: async (data) => {
      try {
        await rota.createRota(data);
        refetchRotas();
        toast({
          variant: 'success',
          title: 'Rota successfully created.',
        });
      } catch (error) {
        toast({
          variant: 'error',
          title: 'Something went wrong. Please try again later.',
        });
      }
      closeModal();
    },
  });

  function findError(
    fieldName: keyof typeof formik.initialValues,
  ): string | undefined {
    const error =
      formik.touched[fieldName] && formik.errors[fieldName]
        ? formik.errors[fieldName]
        : undefined;

    if (Array.isArray(error)) {
      return error.join(', ');
    }

    return error as string | undefined;
  }

  const handleSelectPeople = (id: string, checked: boolean) => {
    const updatedPeople = checked
      ? [...formik.values.employees, id]
      : formik.values.employees.filter((personId) => personId !== id);
    formik.setFieldValue('employees', updatedPeople);
  };

  const handleTimeChange = (field: 'start_time' | 'end_time', value: string) => {
    formik.setFieldValue(field, value);

    if (field === 'start_time') {
      const startMinutes = toMinutes(value);
      const endMinutes = toMinutes(formik.values.end_time);
      if (startMinutes !== null && (endMinutes === null || endMinutes <= startMinutes)) {
        formik.setFieldValue('end_time', addHours(value, 8));
      }
    }
  };

  const handleStartDateChange = (value: string) => {
    formik.setFieldValue('start_date', value);

    if (!formik.values.end_date || formik.values.end_date < value) {
      formik.setFieldValue('end_date', value);
    }
  };

  const selectedEmployeeNames = peopleOptions
    .filter((person: any) => formik.values.employees.includes(person.id))
    .map((person: any) => person.full_name)
    .filter(Boolean);

  const durationLabel = formatDuration(
    formik.values.start_time,
    formik.values.end_time,
  );

  return (
    <Modal open={open} onClose={closeModal} maxWidth="lg">
      {!isShownAssignEmployees ? (
        <>
          <Modal.Header onClose={closeModal}>
            <Modal.Title>Create new rota</Modal.Title>
            <Modal.Description>
              Build a repeatable shift pattern with clear hours, dates, and assigned employees.
            </Modal.Description>
          </Modal.Header>
          
          <Modal.Body>
            <form
              onSubmit={formik.handleSubmit}
              className="flex flex-col gap-6"
            >
              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                <div className="grid gap-4 md:grid-cols-[minmax(0,1.35fr)_minmax(0,0.65fr)]">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="name" className="text-sm font-medium text-slate-700">
                      Name
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter rota name"
                      id="name"
                      name="name"
                      error={findError('name')}
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="w-full"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-slate-700">Quick shift</span>
                    <div className="grid grid-cols-2 gap-2">
                      {SHIFT_PRESETS.map((preset) => (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => {
                            formik.setFieldValue('name', preset.label);
                            formik.setFieldValue('start_time', preset.start);
                            formik.setFieldValue('end_time', preset.end);
                          }}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                        >
                          <div>{preset.label}</div>
                          <div className="mt-0.5 text-xs font-normal text-slate-500">
                            {preset.start} to {preset.end}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="rounded-2xl border border-slate-200 p-4">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                      <ClockIcon className="h-4 w-4 text-slate-400" />
                      Work time
                    </div>
                    <p className="mt-1 text-sm text-slate-500">
                      Use precise times or start with a common preset.
                    </p>
                  </div>
                  {durationLabel ? (
                    <div className="rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700">
                      {durationLabel} shift
                    </div>
                  ) : null}
                </div>

                <div className="mb-4 flex flex-wrap gap-2">
                  {TIME_PRESETS.map((preset) => (
                    <button
                      key={preset.value}
                      type="button"
                      onClick={() => handleTimeChange('start_time', preset.value)}
                      className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
                    >
                      Start {preset.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => formik.setFieldValue('end_time', addHours(formik.values.start_time, 8))}
                    className="rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-100"
                  >
                    Auto-set 8h end
                  </button>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="start_time"
                      className="text-sm font-medium text-slate-700"
                    >
                      Start time
                    </label>
                    <Input
                      type="time"
                      id="start_time"
                      name="start_time"
                      error={findError('start_time')}
                      value={formik.values.start_time}
                      onChange={(event) =>
                        handleTimeChange('start_time', event.target.value)
                      }
                      onBlur={formik.handleBlur}
                      step={900}
                      className="w-full"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="end_time"
                      className="text-sm font-medium text-slate-700"
                    >
                      End time
                    </label>
                    <Input
                      type="time"
                      id="end_time"
                      name="end_time"
                      error={findError('end_time')}
                      value={formik.values.end_time}
                      onChange={(event) =>
                        handleTimeChange('end_time', event.target.value)
                      }
                      onBlur={formik.handleBlur}
                      step={900}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
              
              <div className="rounded-2xl border border-slate-200 p-4">
                <div className="mb-4">
                  <div className="text-sm font-semibold text-slate-900">Work days</div>
                  <p className="mt-1 text-sm text-slate-500">
                    Define when this rota starts and, if needed, when it should stop.
                  </p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="start_date"
                      className="text-sm font-medium text-slate-700"
                    >
                      Start date
                    </label>
                    <Input
                      type="date"
                      id="start_date"
                      name="start_date"
                      value={formik.values.start_date}
                      onChange={(event) => handleStartDateChange(event.target.value)}
                      onBlur={formik.handleBlur}
                      error={findError('start_date')}
                      className="w-full"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="end_date"
                      className="text-sm font-medium text-slate-700"
                    >
                      End date
                    </label>
                    <Input
                      type="date"
                      id="end_date"
                      name="end_date"
                      value={formik.values.end_date}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={findError('end_date')}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
              
              <div className="rounded-2xl border border-slate-200 p-4">
                <div className="mb-4 text-sm font-semibold text-slate-900">Color</div>
                <div className="flex flex-wrap gap-3">
                  {colorOptions.map((option) => (
                    <button
                      type="button"
                      key={option.id}
                      onClick={() => formik.setFieldValue('color', option.hex)}
                      aria-label={`Select rota color ${option.hex}`}
                      className={`relative h-9 w-9 cursor-pointer rounded-full border-2 transition ${
                        formik.values.color === option.hex
                          ? 'scale-110 border-slate-900 shadow-md'
                          : 'border-white ring-1 ring-slate-200 hover:scale-105 hover:ring-slate-300'
                      }`}
                      style={{ backgroundColor: option.hex }}
                    >
                      {formik.values.color === option.hex ? (
                        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-slate-900">
                          ✓
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="rounded-2xl border border-slate-200 p-4">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                      <UserGroupIcon className="h-4 w-4 text-slate-400" />
                      Assigned employees
                    </div>
                    <p className="mt-1 text-sm text-slate-500">
                      Add staff now or leave it empty and assign them later.
                    </p>
                  </div>
                  <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                    {formik.values.employees.length}
                  </div>
                </div>

                {selectedEmployeeNames.length > 0 ? (
                  <div className="mb-4 flex flex-wrap gap-2">
                    {selectedEmployeeNames.slice(0, 4).map((name: string) => (
                      <span
                        key={name}
                        className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700"
                      >
                        {name}
                      </span>
                    ))}
                    {selectedEmployeeNames.length > 4 ? (
                      <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
                        +{selectedEmployeeNames.length - 4} more
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <div className="mb-4 rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-500">
                    No employees assigned yet.
                  </div>
                )}

                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-between rounded-xl"
                  onClick={() => setIsShownAssignEmployyes(true)}
                >
                  <span>Assign employees</span>
                  <ChevronRightIcon className="h-5 w-5" />
                </Button>
              </div>
              
              <input
                type="hidden"
                name="employees"
                value={formik.values.employees.join(',')}
              />
            </form>
          </Modal.Body>
          
          <Modal.Footer>
            <Button
              variant="outline"
              onClick={closeModal}
            >
              Cancel
            </Button>
            <Button color="red" onClick={() => formik.submitForm()}>
              Create rota
            </Button>
          </Modal.Footer>
        </>
      ) : (
        <AssignEmployee
          setIsShownAssignEmployyes={setIsShownAssignEmployyes}
          peopleOptions={peopleOptions}
          handleSelectPeople={handleSelectPeople}
          selectedEmployees={formik.values.employees}
        />
      )}
    </Modal>
  );
}
